/**
 * swUpdate.js — iter307 PWA "New version available" controller.
 *
 * Strict scope:
 *   • Registers /sw.js (idempotent — shares the same registration as
 *     pushNotifications.js → browser dedupes by scope+scriptURL).
 *   • Detects when a new SW reaches the `installed` state while an
 *     existing controller is still serving the page → that is the moment
 *     to surface "New version available".
 *   • Exposes a tiny pub/sub API for React components to subscribe to
 *     the "update available" signal.
 *   • Activates the waiting worker via `postMessage({type:'SKIP_WAITING'})`
 *     and reloads the page exactly once after `controllerchange` fires.
 *
 * Out of scope (untouched by this file):
 *   • Push subscriptions / VAPID — handled by utils/pushNotifications.js.
 *   • PWA install prompt — handled by components/PWAInstallBanner.jsx.
 *   • Any asset caching — sw.js does not cache static assets.
 *
 * Reload-loop guards:
 *   1) `RELOADED_FLAG` (sessionStorage) is set right before reload and
 *      cleared on load. If it is already set when controllerchange fires,
 *      we skip the reload — defends against pages that get controlled
 *      mid-session by an SW that was already active in another tab.
 *   2) `DISMISSED_KEY` (sessionStorage) stores the *waiting worker
 *      scriptURL* the user said "Later" on. The banner stays hidden until
 *      a different waiting worker (= a newer deploy) appears.
 */

const SCRIPT_URL = '/sw.js';
const SCOPE = '/';
const RELOADED_FLAG = 'wassla.sw.update.reloaded.v1';
const DISMISSED_KEY = 'wassla.sw.update.dismissed.v1';

// 30 minutes — periodic check so long-lived sessions notice new deploys
// without forcing the user to refresh manually.
const PERIODIC_CHECK_MS = 30 * 60 * 1000;

let registration = null;
let initStarted = false;
const listeners = new Set();

function isSupported() {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator;
}

function emit(state) {
  for (const fn of listeners) {
    try { fn(state); } catch { /* ignore listener errors */ }
  }
}

function getDismissedScriptURL() {
  try { return sessionStorage.getItem(DISMISSED_KEY) || ''; } catch { return ''; }
}

function setDismissedScriptURL(url) {
  try { sessionStorage.setItem(DISMISSED_KEY, url || ''); } catch { /* ignore */ }
}

function clearDismissedIfStale(currentWaitingURL) {
  // If a *different* waiting worker is now present, the previously
  // dismissed banner is no longer relevant. Clear so banner can show.
  const prev = getDismissedScriptURL();
  if (prev && prev !== currentWaitingURL) {
    setDismissedScriptURL('');
  }
}

function trackInstalling(reg, worker) {
  if (!worker) return;
  worker.addEventListener('statechange', () => {
    if (worker.state === 'installed' && navigator.serviceWorker.controller) {
      // A controller existed → this is an UPDATE, not a fresh install.
      const waitingURL = (reg.waiting && reg.waiting.scriptURL) || worker.scriptURL || '';
      clearDismissedIfStale(waitingURL);
      if (getDismissedScriptURL() === waitingURL) return; // user said "Later" for this exact build
      emit({ available: true, scriptURL: waitingURL });
    }
  });
}

function bindRegistration(reg) {
  registration = reg;

  // 1) A worker is already waiting (e.g. tab was reopened after deploy).
  if (reg.waiting && navigator.serviceWorker.controller) {
    const waitingURL = reg.waiting.scriptURL || '';
    clearDismissedIfStale(waitingURL);
    if (getDismissedScriptURL() !== waitingURL) {
      emit({ available: true, scriptURL: waitingURL });
    }
  }

  // 2) A worker is currently installing.
  if (reg.installing) {
    trackInstalling(reg, reg.installing);
  }

  // 3) Future updates discovered later.
  reg.addEventListener('updatefound', () => {
    trackInstalling(reg, reg.installing);
  });
}

function bindControllerChange() {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    try {
      if (sessionStorage.getItem(RELOADED_FLAG) === '1') return; // already reloaded once
      sessionStorage.setItem(RELOADED_FLAG, '1');
    } catch { /* ignore */ }
    // Defer one tick to let any in-flight UI work settle.
    setTimeout(() => { try { window.location.reload(); } catch { /* ignore */ } }, 0);
  });

  // Clear the flag on load so the next deploy cycle works.
  window.addEventListener('load', () => {
    try { sessionStorage.removeItem(RELOADED_FLAG); } catch { /* ignore */ }
  }, { once: true });
}

function startPeriodicCheck() {
  if (!registration) return;
  const tick = () => {
    if (!registration) return;
    registration.update().catch(() => { /* offline / network — ignore */ });
  };
  // Visibility-driven check so we catch new deploys when the user returns.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tick();
  });
  // Periodic safety net.
  setInterval(tick, PERIODIC_CHECK_MS);
}

/**
 * Subscribe to update-available notifications.
 * Returns an unsubscribe fn.
 */
export function subscribeUpdateAvailable(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Activate the waiting worker and reload once.
 * No-op if there is no waiting worker.
 */
export function applyUpdate() {
  if (!registration || !registration.waiting) return false;
  try {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    return true;
  } catch {
    return false;
  }
}

/**
 * Mark the current waiting build as "Later" so the banner stays hidden
 * until a *different* build is waiting.
 */
export function dismissUpdate() {
  const url = (registration && registration.waiting && registration.waiting.scriptURL) || '';
  setDismissedScriptURL(url);
}

/**
 * Initialize at app boot. Safe to call multiple times.
 */
export function initSWUpdates() {
  if (!isSupported() || initStarted) return;
  initStarted = true;

  bindControllerChange();

  navigator.serviceWorker
    .register(SCRIPT_URL, { scope: SCOPE })
    .then((reg) => {
      bindRegistration(reg);
      startPeriodicCheck();
    })
    .catch((err) => {
      // SW registration can fail in private/incognito modes or when the
      // host blocks it. Swallow — the rest of the app must keep working.
      // eslint-disable-next-line no-console
      console.warn('[sw-update] register failed', err);
    });
}
